import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import { Breadcrumb, Spin } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

interface PICard {
  pi_id: number;
  pi_order_no: string;
  pi_date: string;
  pi_lead_time: number;
  ledger_company_name: string;
  ledger_first_name: string;
  ledger_last_name: string;
  items: { pid_product_id: number; pid_qty: number }[];
}

const getCardBorderColor = (pi_date: string, lead_time: number): string => {
  const deadline = dayjs(pi_date).add(lead_time, 'day');
  const today = dayjs();
  const daysLeft = deadline.diff(today, 'day');

  if (daysLeft < 0) return '#ff4d4f';       // 🔴 Red - PO delay
  if (daysLeft <= 3) return '#faad14';       // 🟡 Yellow - Work order delay
  return '#52c41a';                          // 🟢 Green - On time
};

const getCardStatus = (pi_date: string, lead_time: number): string => {
  const deadline = dayjs(pi_date).add(lead_time, 'day');
  const today = dayjs();
  const daysLeft = deadline.diff(today, 'day');

  if (daysLeft < 0) return `${Math.abs(daysLeft)} days overdue`;
  if (daysLeft === 0) return 'Due today';
  return `${daysLeft} days left`;
};

const TrackPI: React.FC = () => {
  const [invoices, setInvoices] = useState<PICard[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const data = await apiRequest<PICard[]>({
        method: 'get',
        url: '/get-performa-invoices',
      });
      setInvoices(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item href="/"><HomeOutlined /></Breadcrumb.Item>
        <Breadcrumb.Item>Track PI (Performa Invoices)</Breadcrumb.Item>
      </Breadcrumb>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" />
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
          }}
        >
          {invoices.map((inv) => {
            const borderColor = getCardBorderColor(inv.pi_date, inv.pi_lead_time);
            const statusText = getCardStatus(inv.pi_date, inv.pi_lead_time);
            const deadline = dayjs(inv.pi_date).add(inv.pi_lead_time, 'day');

            return (
              <div
                key={inv.pi_id}
                onClick={() => navigate(`/track-pi/${inv.pi_id}`)}
                style={{
                  width: 220,
                  border: `3px solid ${borderColor}`,
                  borderRadius: 12,
                  padding: '16px 18px',
                  cursor: 'pointer',
                  background: '#fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  position: 'relative',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-3px)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = '0 6px 16px rgba(0,0,0,0.13)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                }}
              >
                {/* Color dot */}
                <div
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: borderColor,
                  }}
                />

                {/* PI Number */}
                <div style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a' }}>
                  #{inv.pi_order_no}
                </div>

                {/* PI ID */}
                <div style={{ fontSize: 12, color: '#999', marginBottom: 10 }}>
                  PI-{inv.pi_id}
                </div>

                {/* Divider */}
                <div style={{ borderTop: '1px solid #f0f0f0', marginBottom: 10 }} />

                {/* Ledger */}
                <div style={{ fontSize: 13, color: '#555', marginBottom: 6 }}>
                  🏢 {inv.ledger_company_name ||
                    `${inv.ledger_first_name} ${inv.ledger_last_name}`}
                </div>

                {/* Products count */}
                <div style={{ fontSize: 13, color: '#555', marginBottom: 6 }}>
                  📦 {inv.items?.length || 0} product{inv.items?.length !== 1 ? 's' : ''}
                </div>

                {/* Deadline */}
                <div style={{ fontSize: 13, color: '#555', marginBottom: 10 }}>
                  📅 {deadline.format('DD MMM YYYY')}
                </div>

                {/* Status Badge */}
                <div
                  style={{
                    display: 'inline-block',
                    background: borderColor + '1a', // 10% opacity background
                    color: borderColor,
                    border: `1px solid ${borderColor}`,
                    borderRadius: 20,
                    padding: '2px 10px',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  {statusText}
                </div>
              </div>
            );
          })}

          {invoices.length === 0 && (
            <div style={{ color: '#aaa', fontSize: 15 }}>No PI found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default TrackPI;